"use client";

import type { MindstringPrototype } from "@/generated/fastapi";
import type { Meilisearch, SearchResponse } from "meilisearch";
import { buildHybridSearchParams, buildMeiliFilterString } from "./helpers";

export type MeiliThoughtIndexName = "suggested_thoughts" | "thoughts";

function buildMeiliSearchOptions({
  limit,
  semanticRatio,
}: {
  limit?: number;
  semanticRatio?: number;
}) {
  const options: Record<string, unknown> = {};

  if (limit) {
    options.limit = limit;
  }

  if (semanticRatio === undefined || semanticRatio === null) {
    return options;
  }

  const hybridConfig = buildHybridSearchParams(semanticRatio);
  if (hybridConfig) {
    options.hybrid = hybridConfig;
  }

  return options;
}

function parseSortByOption({
  indexName,
  sortBy,
}: {
  indexName: MeiliThoughtIndexName;
  sortBy?: string;
}): string[] | undefined {
  if (!sortBy) {
    return undefined;
  }

  const [sortIndexName, field, direction] = sortBy.split(":");
  if (
    sortIndexName !== indexName ||
    !field ||
    (direction !== "asc" && direction !== "desc")
  ) {
    return undefined;
  }

  return [`${field}:${direction}`];
}

export async function searchThoughtDocumentsWithClient<
  T extends Record<string, unknown>,
>({
  meiliJsClient,
  indexName,
  originId,
  situationId,
  prototype,
  items_ids,
  languageFilters,
  limit,
  queryText,
  semanticRatio = 0,
  sortBy,
}: {
  meiliJsClient?: Meilisearch;
  indexName: MeiliThoughtIndexName;
  originId?: string;
  situationId?: string;
  prototype?: MindstringPrototype;
  items_ids?: string[];
  languageFilters?: string[];
  limit?: number;
  queryText?: string;
  semanticRatio?: number;
  sortBy?: string;
}): Promise<T[]> {
  if (!meiliJsClient) {
    return [];
  }

  const filterToApply = buildMeiliFilterString({
    originId,
    situationId,
    prototype,
    items_ids,
    languageFilters,
  });

  const options = buildMeiliSearchOptions({
    limit,
    semanticRatio,
  });

  if (filterToApply) {
    options.filter = filterToApply;
  }

  const sortOption = parseSortByOption({ indexName, sortBy });
  if (sortOption) {
    options.sort = sortOption;
  }

  const searchIndexName = sortOption ? indexName : (sortBy ?? indexName);

  try {
    const results: SearchResponse<T> = await meiliJsClient
      .index(searchIndexName)
      .search<T>(queryText, options);
    return results.hits;
  } catch {
    if (sortBy && sortBy !== indexName) {
      try {
        const fallbackResults: SearchResponse<T> = await meiliJsClient
          .index(indexName)
          .search<T>(queryText, options);
        return fallbackResults.hits;
      } catch {
        return [];
      }
    }

    return [];
  }
}
